'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { t } from 'i18next'
import Image from 'next/image'

import Button from 'app/app/(client)/components/atoms/Button/Button'
import Divider from 'app/app/(client)/components/atoms/Divider/Divider'
import Icon from 'app/app/(client)/components/atoms/Icon/Icon'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import IconwithText from 'app/app/(client)/components/molecules/IconwithText/IconwithText'
import NavlinkList from 'app/app/(client)/components/molecules/NavlinkList/NavlinkList'
import ActionButton from 'app/app/(client)/components/organisms/ActionButton/ActionButton'
import { T_NavLink } from 'types/types'
import { useNetwork } from 'app/app/(client)/hooks/useNetwork'
import { useAppSelector } from 'app/app/(client)/hooks/redux-hook'
import Tooltip from '../../molecules/Tooltip/Tooltip'
import { Constants } from 'app/app/(client)/utils/client-constants'
import Badge from '../../molecules/Badge/Badge'
import {
  getEnvironmentLabel,
  isDevelopmentEnvironment,
  isProductionEnvironment
} from 'app/app/(client)/utils/environment-utils'
import { useNavigate } from 'app/app/(client)/hooks/useNavigate'
import { debugTranslation } from 'app/app/(client)/i18n/debug-translations'
import NotificationDot from '../../atoms/NotificationDot/NotificationDot'

type HeaderProps = {
  id: string
  className?: string
  navlinks?: T_NavLink[]
  onClickHandlers: {
    onLogoutClick: () => void
    onLanguageChange: () => void
    onRefreshClick: () => void
    onSyncClick: () => void
  }
  selectedLanguage: string
  userName: string
  showRefreshButton: boolean
}

const Header: React.FC<HeaderProps> = ({
  id,
  className = '',
  navlinks = [],
  onClickHandlers,
  selectedLanguage,
  userName,
  showRefreshButton
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [debugTranslations, setDebugTranslations] = useState(false)
  const [environmentLabel, setEnvironmentLabel] = useState('')
  const [isDevelopment, setIsDevelopment] = useState(false)
  const [isNotProduction, setIsNotProduction] = useState(false)
  const [syncClassName, setSyncClassName] = useState('')
  const router = useNavigate()
  const isOnline = useNetwork()
  const handleNavbuttonClick = (link: string) => {
    setMenuOpen(false)
    router.push(link)
  }
  const unsavedOfferCount = useAppSelector(state => state.unSavedOfferCount.data || 0)
  const syncInProgress = useAppSelector(state => state.syncInProgress.data || false)
  const toggleDebugTranslations = () => {
    setDebugTranslations(!debugTranslations)
    if (debugTranslations) {
      window.location.search = window.location.search.replace(debugTranslation.param, '')
    } else {
      window.location.search += (window.location.search.length ? '&' : '') + debugTranslation.param
    }
  }

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    setDebugTranslations(window.location.search.includes(debugTranslation.key))
    setIsDevelopment(isDevelopmentEnvironment(window.environment))
    setIsNotProduction(!isProductionEnvironment(window.environment))
    setEnvironmentLabel(getEnvironmentLabel(window.environment))
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (debugTranslations) {
      document.body.classList.add('break-all')
    } else {
      document.body.classList.remove('break-all')
    }
  }, [debugTranslations])

  const getCloudIcon = useMemo(
    () => (!isOnline ? 'icon-cloud-disconnected' : unsavedOfferCount > 0 ? 'icon-cloud-upload' : 'icon-cloud'),
    [isOnline, unsavedOfferCount]
  )

  useEffect(() => {
    setSyncClassName(`${getCloudIcon} text-18px sm:text-30px ${syncInProgress ? 'animate-pulse' : ''}`)
  }, [getCloudIcon, syncInProgress])

  return (
    <header
      id={id}
      className={`w-full bg-white text-black pl-4 pr-2 sm:px-4 shadow-md ${className} max-h-14 md:max-h-16`}
    >
      <div className="w-full flex flex-row flex-wrap justify-between">
        <div className="flex flex-wrap items-center justify-between order-first sm:order-1 sm:py-3 sm:gap-8">
          <div
            className="flex items-center gap-3 sm:gap-2 md:gap-3 my-3 sm:my-0 cursor-pointer"
            onClick={() => router.push('/')}
          >
            <Image
              src="/images/logo/logo_primary.svg"
              width={82}
              height={32}
              alt="kone_logo"
              sizes="(max-width: 480px) 72px, (min-width: 481px) 82px"
              className="w-[72px] h-[28px] md:w-[82px] md:h-[32px] object-contain"
            />
            <Text
              id="header-brand-title"
              as="div"
              className="text-16px md:text-18px md:leading-6 spacing tracking-normal font-kone-semibold"
            >
              {Constants.HEADER_NAME}
            </Text>
          </div>
          <Divider orientation="vertical" className="hidden sm:block w-px h-[37px] bg-background-divider mx-2" />
        </div>
        <NavlinkList
          id="navlink-list"
          className="nav-links sm:ml-8"
          menuOpen={menuOpen}
          navlinks={navlinks}
          handleClick={handleNavbuttonClick}
        />
        <div className=" flex items-center justify-end order-2 sm:order-3 gap-2 my-3 sm:mx-0 sm:my-0">
          {isNotProduction && !isMobile && (
            <>
              <Badge
                id="badge-id"
                type="status"
                variant="success"
                label={`${environmentLabel}`}
                icon="icon-name"
                className="text-14px capitalize"
              />
              <Divider orientation="vertical" className="hidden sm:block w-px !h-[32px] bg-background-divider mx-2" />
            </>
          )}
          {isNotProduction && !isMobile && (
            <>
              {isDevelopment && (
                <Tooltip id="storybook-tooltip" content="Story Book" position="auto">
                  <Button
                    id="storybook-button"
                    className="btn-icon flex !h-[34px] !w-[34px]"
                    size="small"
                    variant="tertiary"
                    aria-label="Story Book"
                    onClick={() => handleNavbuttonClick(Constants.HEADER_STORYBOOK_LINK)}
                  >
                    <Icon name="debug" className="icon-book text-18px sm:text-30px" />
                  </Button>
                </Tooltip>
              )}
              <Button
                id="debug-button"
                className="btn-icon flex !h-[34px] !w-[34px]"
                size="small"
                variant="tertiary"
                aria-label="Debug Translations"
                onClick={toggleDebugTranslations}
              >
                <Icon
                  name="debug"
                  className={`${debugTranslations ? 'icon-area' : 'icon-txt'} text-18px sm:text-30px`}
                />
              </Button>
            </>
          )}
          {showRefreshButton && (
            <Button
              id="opp-refresh-button"
              className="btn-icon flex !h-[34px] !w-[34px]"
              size="small"
              variant="tertiary"
              aria-label="Refresh"
              onClick={onClickHandlers.onRefreshClick}
            >
              <Icon name="profile" className="icon-refresh text-18px sm:text-30px" />
            </Button>
          )}
          <Button
            id="opp-sync-button"
            className="btn-icon !h-[34px] !w-[34px] hidden sm:flex relative"
            size="small"
            variant="secondary"
            aria-label="Sync"
            onClick={onClickHandlers.onSyncClick}
            disabled={syncInProgress || !isOnline}
          >
            <Icon name="profile" className={syncClassName} />
            <NotificationDot
              count={unsavedOfferCount}
              variant="desktop"
              className={syncInProgress || !isOnline ? 'animate-pulse' : ''}
            />
          </Button>
          <ActionButton
            id="header-profile"
            iconName={isMobile ? 'icon-view-list' : 'icon-user'}
            variant="secondary"
            size="xsmall"
            className="btn !px-2 !py-2 !h-[34px] !w-[34px] !text-18px/[24px]"
            position="auto"
          >
            <div className="flex flex-col justify-between h-full test-class">
              <div className="flex flex-col">
                <div className="py-4 px-2 rounded-[8px] cursor-default">
                  <IconwithText
                    id="header-site-username"
                    iconName="icon-user"
                    className="text-20px"
                    variant="primary"
                    textContent={[{ text: userName, style: '!text-16px ml-[16px]' }]}
                  />
                </div>
                <div
                  className="py-4 px-2 rounded-[8px] hover:bg-background-nav-hover cursor-pointer "
                  onClick={onClickHandlers.onLanguageChange}
                >
                  <IconwithText
                    id="header-site-language"
                    iconName="icon-language"
                    className="text-20px"
                    variant="primary"
                    textContent={[{ text: selectedLanguage, style: '!text-16px  ml-[16px]' }]}
                  />
                </div>
                <div className="px-2 block sm:hidden">
                  <Divider orientation="horizontal" className="divider my-1" />
                </div>
                <div
                  className="py-4 px-2 rounded-[8px] hover:bg-background-nav-hover cursor-pointer sm:hidden flex flex-row items-center justify-between relative"
                  onClick={onClickHandlers.onSyncClick}
                >
                  <IconwithText
                    id="header-mobile-sync"
                    iconName={getCloudIcon}
                    className={`text-20px ${syncInProgress ? 'animate-pulse' : ''}`}
                    variant="primary"
                    textContent={[{ text: t('navigation.refresh.sync'), style: '!text-16px  ml-[16px]' }]}
                  />
                  <NotificationDot
                    count={unsavedOfferCount}
                    variant="mobile"
                    className={syncInProgress || !isOnline ? 'animate-pulse' : ''}
                  />
                </div>
              </div>
              <div>
                <div className="px-2">
                  <Divider orientation="horizontal" className="divider my-1" />
                </div>
                <div
                  className="py-4 px-2 rounded-[8px] hover:bg-background-nav-hover cursor-pointer "
                  onClick={onClickHandlers.onLogoutClick}
                >
                  <IconwithText
                    id="header-site-logout"
                    iconName="icon-logout"
                    className="text-20px"
                    variant="primary"
                    textContent={[{ text: t('navigation.refresh.logout'), style: '!text-16px  ml-[16px]' }]}
                  />
                </div>
              </div>
            </div>
          </ActionButton>
        </div>
      </div>
    </header>
  )
}

export default Header
